import { useState } from "react";
import {
  ArrowRight,
  Radar,
  Zap,
  Clock,
  ShieldCheck,
  Search,
  Workflow,
  FileText,
  Plus,
  Minus,
  Sparkles,
  Swords,
  Crosshair,
  Target,
  Check,
  X,
  Minus as Dash,
  Gauge,
  Bookmark,
  Download,
  Cpu,
} from "lucide-react";
import { INTEL_AGENTS } from "./brand";
import { SiteFooter } from "./SiteFooter";

interface LandingHeroProps {
  /** Tracked competitors from the saved profile (chips become one-tap launches). */
  trackedCompetitors: string[];
  /** Launch an analysis for a specific competitor (or scroll to the input). */
  onLaunch: (competitor?: string) => void;
}

// ── Section content ───────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Specialists", href: "#specialists" },
  { label: "Why NovaSynth", href: "#why" },
  { label: "What you get", href: "#showcase" },
  { label: "FAQ", href: "#faq" },
];

const VALUE_PROPS = [
  {
    Icon: Clock,
    title: "Minutes, not weeks",
    body: "A full competitive brief assembled while you grab a coffee - not after a quarter of desk research.",
  },
  {
    Icon: Radar,
    title: "Five signals, one view",
    body: "Video, filings, web, news, and threat scoring run in parallel and converge into a single report.",
  },
  {
    Icon: Zap,
    title: "Live, as it happens",
    body: "Watch each specialist work in real time and read the synthesis as it streams in.",
  },
];

const TRUST_STRIP = [
  "PRODUCT TEAMS",
  "GO-TO-MARKET",
  "STRATEGY & CORP DEV",
  "FOUNDERS",
  "SALES ENABLEMENT",
  "ANALYST RELATIONS",
];

const HOW_IT_WORKS = [
  {
    Icon: Search,
    step: "01",
    title: "Name a competitor",
    body: "Type any company or domain. No connectors, no setup, no spreadsheets to wire up first.",
  },
  {
    Icon: Workflow,
    step: "02",
    title: "Watch the swarm work",
    body: "Five specialists fan out across video, filings, the open web, and news - live, in parallel, in front of you.",
  },
  {
    Icon: FileText,
    step: "03",
    title: "Act on the brief",
    body: "Everything fuses into one threat-scored report and a battle-ready card your team can use today.",
  },
];

const STATS = [
  { value: "5", label: "intelligence specialists", sub: "working every brief in parallel" },
  { value: "< 5 min", label: "to a full brief", sub: "from a name to a decision" },
  { value: "100%", label: "claims with sources", sub: "every signal is cited" },
  { value: "24/7", label: "always watching", sub: "tracking never sleeps" },
];

// ── Positioning: how NovaSynth compares to the real alternatives ──────────────
// Honest capability comparison (no fabricated customers). Each row is a job to
// be done; columns are the realistic ways teams do it today vs NovaSynth.
const COMPARE_ROWS: { capability: string; manual: Cell; alerts: Cell; suite: Cell; nova: Cell }[] = [
  {
    capability: "Time to a full brief",
    manual: { kind: "bad", text: "Days to weeks" },
    alerts: { kind: "mid", text: "Headlines only" },
    suite: { kind: "mid", text: "Hours, after setup" },
    nova: { kind: "good", text: "Minutes" },
  },
  {
    capability: "Coverage across signals",
    manual: { kind: "mid", text: "Whatever you find" },
    alerts: { kind: "bad", text: "News alerts only" },
    suite: { kind: "good", text: "Broad" },
    nova: { kind: "good", text: "Video, filings, web, news" },
  },
  {
    capability: "Every claim sourced",
    manual: { kind: "mid", text: "If you note them" },
    alerts: { kind: "good", text: "Links to articles" },
    suite: { kind: "mid", text: "Varies" },
    nova: { kind: "good", text: "Cited on every line" },
  },
  {
    capability: "Threat scored for you",
    manual: { kind: "bad", text: "Your own read" },
    alerts: { kind: "bad", text: "None" },
    suite: { kind: "mid", text: "Generic scoring" },
    nova: { kind: "good", text: "Personalised to your business" },
  },
  {
    capability: "Setup to first value",
    manual: { kind: "good", text: "None" },
    alerts: { kind: "good", text: "Low" },
    suite: { kind: "bad", text: "Onboarding + config" },
    nova: { kind: "good", text: "Type a name, go" },
  },
];

// Concrete deliverables - what you actually get out of a run.
const CAPABILITIES: { Icon: typeof Radar; title: string; body: string }[] = [
  {
    Icon: Radar,
    title: "Five intelligence specialists",
    body: "News, documents, web, and video run in parallel, with a threat-scoring layer fusing them - no tab-juggling, no manual stitching.",
  },
  {
    Icon: Gauge,
    title: "A personalised threat score",
    body: "One fused score out of ten, framed around your business - the drivers behind it, and a plain read on why this competitor matters to you.",
  },
  {
    Icon: Swords,
    title: "A battle-ready card",
    body: "Top threats and counter-moves your team can use in a live deal today - every line backed by a clickable source.",
  },
  {
    Icon: Bookmark,
    title: "A watchlist with trend",
    body: "Track every competitor in one view, see the latest threat read at a glance, and watch the score move over time.",
  },
  {
    Icon: FileText,
    title: "One fused brief",
    body: "Not a wall of links - a single, readable report that assembles itself live and reads like a finished analyst memo.",
  },
  {
    Icon: Download,
    title: "Export anywhere",
    body: "Copy the brief, save it as a PDF, or hand the battlecard to your team - the work leaves with you, ready to share.",
  },
];

interface Cell {
  kind: "good" | "mid" | "bad";
  text: string;
}

const FAQS = [
  {
    q: "What exactly does a brief include?",
    a: "A fused threat score with the drivers behind it, a plain-language read on why a competitor matters to you, the top threats and counter-moves, and a battle-ready card - every claim backed by a clickable source.",
  },
  {
    q: "Where do the signals come from?",
    a: "Five specialists cover launches and keynote video, filings and financials, the open web (pricing, reviews, hiring), and breaking news - then a scoring layer fuses them into one assessment.",
  },
  {
    q: "How fresh is the intelligence?",
    a: "Briefs are assembled on demand from current signals, and you can force a fresh pass any time. Tracking runs continuously so you are never working from stale data.",
  },
  {
    q: "Do I need to connect anything to get started?",
    a: "No. Type a company name or domain and the first brief starts immediately - no connectors, data uploads, or configuration required.",
  },
  {
    q: "Can I tailor it to my company?",
    a: "Yes. Your profile personalises every assessment so the threat read and counter-moves are framed around your business, not a generic market view.",
  },
];

// ── Component ───────────────────────────────────────────────────────────────

export function LandingHero({ trackedCompetitors, onLaunch }: LandingHeroProps) {
  return (
    <div className="relative -mx-4 sm:-mx-6">
      <div className="px-4 sm:px-6">
        <LandingNav onLaunch={onLaunch} />

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative pt-6 pb-2">
          <div className="ns-reveal max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#22d3ee]/25 bg-[#22d3ee]/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-[#22d3ee]">
              <span className="relative flex w-1.5 h-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22d3ee] opacity-60" />
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-[#22d3ee]" />
              </span>
              Live competitive intelligence
            </span>

            <h1 className="mt-5 font-mono text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.04] tracking-tight text-white">
              Know your competitors
              <br className="hidden sm:block" />{" "}
              <span className="ns-gradient-text">before they move.</span>
            </h1>

            <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-400">
              NovaSynth deploys five intelligence specialists across video, filings, the open
              web, and news - then fuses everything into one threat-scored brief. Type a company,
              and watch the picture build itself in real time.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                onClick={() => onLaunch()}
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 hover:shadow-glow-brand focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2 focus:ring-offset-[#020617] cursor-pointer min-h-[44px]"
              >
                Start an analysis
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#1a2640] bg-white/5 px-5 py-3 text-sm font-medium text-slate-300 transition-colors duration-200 hover:border-[#263350] hover:text-white cursor-pointer min-h-[44px]"
              >
                See how it works
              </a>
            </div>

            <p className="mt-3 text-xs text-slate-600">
              No setup required - pick a company below or type your own.
            </p>

            {/* Tracked competitor quick-launch chips */}
            {trackedCompetitors.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  Tracking
                </span>
                {trackedCompetitors.map((c, i) => (
                  <button
                    key={c}
                    onClick={() => onLaunch(c)}
                    className="ns-reveal rounded-lg border border-[#1a2640] bg-white/5 px-3 py-1.5 font-mono text-xs text-slate-300 transition-all duration-200 hover:border-[#22d3ee]/40 hover:text-[#22d3ee] hover:bg-[#22d3ee]/5 cursor-pointer min-h-[36px]"
                    style={{ ["--ns-delay" as string]: `${120 + i * 60}ms` }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── Value props ────────────────────────────────────────────────── */}
        <section className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {VALUE_PROPS.map((v, i) => (
            <div
              key={v.title}
              className="ns-reveal ns-glass rounded-2xl p-5 transition-transform duration-200 hover:-translate-y-0.5"
              style={{ ["--ns-delay" as string]: `${160 + i * 80}ms` }}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
                <v.Icon className="w-[18px] h-[18px]" />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-white">{v.title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">{v.body}</p>
            </div>
          ))}
        </section>

        {/* ── Trust strip ───────────────────────────────────────────────── */}
        <section className="mt-14 ns-reveal" style={{ ["--ns-delay" as string]: "120ms" }}>
          <p className="text-center text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-600">
            Built for the teams that can&apos;t afford to be surprised
          </p>
          <div className="relative mt-4 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_12%,black_88%,transparent)]">
            <div className="flex w-max animate-marquee gap-10">
              {[...TRUST_STRIP, ...TRUST_STRIP].map((label, i) => (
                <span
                  key={`${label}-${i}`}
                  className="whitespace-nowrap font-mono text-xs font-medium tracking-[0.18em] text-slate-600"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        <HowItWorks />
        <Specialists onLaunch={onLaunch} />
        <WhyNovaSynth />
        <Showcase onLaunch={onLaunch} />
        <Results />
        <Faq />
        <FinalCta onLaunch={onLaunch} />
      </div>

      {/* Shared footer (full-bleed via the negative margins on the wrapper). */}
      <SiteFooter variant="landing" />
    </div>
  );
}

// ── Sticky in-landing nav (anchor links + CTA) ────────────────────────────────

function LandingNav({ onLaunch }: { onLaunch: (c?: string) => void }) {
  return (
    <nav className="sticky top-16 z-30 -mx-4 sm:-mx-6 mb-2 border-b border-[#1a2640]/40 bg-[#020617]/70 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-4 px-4 sm:px-6 h-12">
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 transition-colors hover:text-white hover:bg-white/5 cursor-pointer"
            >
              {l.label}
            </a>
          ))}
        </div>
        <span className="md:hidden inline-flex items-center gap-2 text-xs font-medium text-slate-400">
          <Sparkles className="w-3.5 h-3.5 text-[#22d3ee]" />
          The competitive intelligence platform
        </span>
        <button
          onClick={() => onLaunch()}
          className="group inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-[#1a2640] px-3.5 py-1.5 text-xs font-semibold text-slate-200 transition-all duration-200 hover:border-[#22d3ee]/40 hover:text-[#22d3ee] cursor-pointer"
        >
          Start free
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>
    </nav>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <section id="how-it-works" className="mt-20 scroll-mt-28">
      <SectionHeading
        eyebrow="How it works"
        title="From a name to a decision, in three steps"
        sub="No connectors. No configuration. Just type a competitor and watch the brief assemble itself."
      />
      <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
        {HOW_IT_WORKS.map((s, i) => (
          <div
            key={s.step}
            className="ns-reveal relative ns-glass rounded-2xl p-6 transition-transform duration-200 hover:-translate-y-0.5"
            style={{ ["--ns-delay" as string]: `${120 + i * 90}ms` }}
          >
            <span className="absolute right-5 top-5 font-mono text-3xl font-bold text-white/5">{s.step}</span>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
              <s.Icon className="w-5 h-5" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-white">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.body}</p>
            {i < HOW_IT_WORKS.length - 1 && (
              <ArrowRight className="hidden md:block absolute -right-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#22d3ee]/40 z-10" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Specialists showcase ────────────────────────────────────────────────────

function Specialists({ onLaunch }: { onLaunch: (c?: string) => void }) {
  return (
    <section id="specialists" className="mt-20 scroll-mt-28">
      <div className="flex items-end justify-between gap-4 mb-6">
        <SectionHeading
          eyebrow="The intelligence engine"
          title="Five specialists, working as one"
          sub="Each one owns a corner of the picture. Together they leave nothing uncovered."
          align="left"
        />
        <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-500 shrink-0 pb-1">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          Sources cited on every claim
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {INTEL_AGENTS.map((a, i) => (
          <div
            key={a.key}
            className="ns-reveal group relative overflow-hidden rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-white/15"
            style={{ ["--ns-delay" as string]: `${120 + i * 70}ms` }}
          >
            <div
              className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
              style={{ background: a.hex }}
              aria-hidden="true"
            />
            <div className={`relative flex h-11 w-11 items-center justify-center rounded-xl border ${a.chip}`}>
              <a.Icon className={`w-5 h-5 ${a.tint}`} />
            </div>
            <h3 className="relative mt-4 text-[15px] font-semibold text-white">{a.name}</h3>
            <p className="relative mt-2 text-sm leading-relaxed text-slate-400">{a.blurb}</p>
            {/* Tasteful Amazon Nova model branding (a strength for the AWS
                Builder Center audience) - product-facing model NAME only. */}
            <div className="relative mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#22d3ee]/20 bg-[#22d3ee]/[0.06] px-2.5 py-1">
              <Cpu className="w-3 h-3 text-[#22d3ee]/80 shrink-0" aria-hidden="true" />
              <span className="text-[10px] font-medium text-slate-400">
                Powered by <span className="text-[#22d3ee]/90">{a.model}</span>
              </span>
            </div>
          </div>
        ))}

        {/* CTA card to fill the 6th cell */}
        <button
          onClick={() => onLaunch()}
          className="ns-reveal group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-brand-600/30 bg-gradient-to-br from-brand-700/20 to-[#22d3ee]/5 p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500/50 cursor-pointer min-h-[170px]"
          style={{ ["--ns-delay" as string]: "550ms" }}
        >
          <div>
            <h3 className="text-[15px] font-semibold text-white">See it run live</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Launch your first brief and watch every specialist report in real time.
            </p>
          </div>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#22d3ee]">
            Start now
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </button>
      </div>
    </section>
  );
}

// ── Product output showcase (threat score + battlecard visual) ────────────────

function Showcase({ onLaunch }: { onLaunch: (c?: string) => void }) {
  return (
    <section id="showcase" className="mt-20 scroll-mt-28">
      <SectionHeading
        eyebrow="What you get"
        title="A threat-scored brief, ready to act on"
        sub="Not a wall of links - a single assessment with the score, the why, and the counter-moves."
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        <ShowcaseThreatCard />
        <ShowcaseBattlecard />
      </div>

      <div className="mt-7 flex justify-center">
        <button
          onClick={() => onLaunch()}
          className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 hover:shadow-glow-brand cursor-pointer min-h-[44px]"
        >
          Build a brief like this
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>
    </section>
  );
}

/** Inline-SVG threat-score visual - no external images, on-brand gauge. */
function ShowcaseThreatCard() {
  const score = 8;
  const size = 150;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const semi = Math.PI * r;
  const pct = score / 10;
  const arc = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const hex = "#f59e0b";

  const drivers = [
    { label: "Product velocity", pct: 90 },
    { label: "Pricing pressure", pct: 72 },
    { label: "Market momentum", pct: 84 },
  ];

  return (
    <div className="ns-glass ns-reveal rounded-2xl p-6 shadow-card flex flex-col" style={{ ["--ns-delay" as string]: "120ms" }}>
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-600/40 bg-amber-500/10 text-amber-300">
          <Crosshair className="w-4 h-4" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">Threat assessment</p>
          <p className="font-mono text-base font-bold text-white leading-tight">Example Competitor</p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-5">
        <div className="relative shrink-0" style={{ width: size, height: size / 2 + 28 }} aria-hidden="true">
          <svg width={size} height={size / 2 + 8} viewBox={`0 0 ${size} ${size / 2 + 8}`}>
            <path d={arc} fill="none" stroke="#1a2640" strokeWidth={stroke} strokeLinecap="round" />
            <path
              d={arc}
              fill="none"
              stroke={hex}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={semi}
              strokeDashoffset={semi * (1 - pct)}
              style={{ filter: `drop-shadow(0 0 5px ${hex}66)` }}
            />
          </svg>
          <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
            <span className="font-mono text-3xl font-bold leading-none text-amber-400">{score}</span>
            <span className="mt-0.5 text-[10px] font-mono text-slate-500 tracking-wider">/ 10</span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-600/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
            High threat
          </span>
          <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">
            Accelerating launch cadence and an aggressive pricing move put direct pressure on your
            core segment this quarter.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {drivers.map((d) => (
          <div key={d.label}>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-xs font-medium text-slate-300">{d.label}</span>
            </div>
            <div className="h-2 rounded-full bg-[#0a1322] border border-[#1a2640]/60 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                style={{ width: `${d.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Inline battlecard visual - top threats + counter-moves, on-brand. */
function ShowcaseBattlecard() {
  const threats = [
    "New self-serve tier undercuts your entry pricing by ~30%.",
    "Hiring surge in enterprise sales signals an up-market push.",
    "Launch keynote teased an AI feature overlapping your roadmap.",
  ];
  const moves = [
    "Lead with your migration story and total cost of ownership.",
    "Fast-track the differentiator they can't match this quarter.",
    "Arm reps with the source-backed objection handler below.",
  ];

  return (
    <div className="ns-glass ns-reveal rounded-2xl p-6 shadow-card flex flex-col" style={{ ["--ns-delay" as string]: "200ms" }}>
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-700/40 bg-emerald-500/10 text-emerald-300">
          <Swords className="w-4 h-4" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] font-semibold text-emerald-300 uppercase tracking-[0.2em]">Battlecard</p>
          <p className="font-mono text-base font-bold text-white leading-tight">How to win vs them</p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Swords className="w-3.5 h-3.5 text-amber-400" aria-hidden="true" />
            <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">Top threats</p>
          </div>
          <ul className="flex flex-col gap-2">
            {threats.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-amber-400/70" aria-hidden="true" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Target className="w-3.5 h-3.5 text-emerald-400" aria-hidden="true" />
            <p className="text-[10px] font-semibold text-slate-300 uppercase tracking-[0.18em]">Counter-moves</p>
          </div>
          <ul className="flex flex-col gap-2">
            {moves.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                <span className="mt-1.5 w-1 h-1 rounded-full shrink-0 bg-emerald-400/70" aria-hidden="true" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Results / stats band ──────────────────────────────────────────────────────

function Results() {
  return (
    <section id="results" className="mt-20 scroll-mt-28">
      <div className="relative ns-reveal overflow-hidden rounded-3xl border border-[#1a2640] bg-[#0d1424]/70 p-8 sm:p-10">
        <div className="ns-aurora opacity-60" aria-hidden="true" />
        <div className="relative">
          <SectionHeading
            eyebrow="Outcomes"
            title="Intelligence that moves the needle"
            sub="What teams stop doing manually - and start trusting - once the briefs are live."
          />
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className="ns-reveal text-center sm:text-left"
                style={{ ["--ns-delay" as string]: `${120 + i * 70}ms` }}
              >
                <p className="font-mono text-3xl sm:text-4xl font-bold ns-gradient-text">{s.value}</p>
                <p className="mt-2 text-sm font-semibold text-white">{s.label}</p>
                <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Why NovaSynth: positioning vs the real alternatives + capabilities ────────

function CompareCell({ cell, emphasise = false }: { cell: Cell; emphasise?: boolean }) {
  const Icon = cell.kind === "good" ? Check : cell.kind === "bad" ? X : Dash;
  const iconColor =
    cell.kind === "good"
      ? emphasise
        ? "text-emerald-400"
        : "text-emerald-400/80"
      : cell.kind === "bad"
        ? "text-slate-600"
        : "text-slate-500";
  const textColor = emphasise ? "text-white" : "text-slate-400";
  return (
    <div className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconColor}`} aria-hidden="true" />
      <span className={`text-xs leading-snug ${textColor}`}>{cell.text}</span>
    </div>
  );
}

function WhyNovaSynth() {
  return (
    <section id="why" className="mt-20 scroll-mt-28">
      <SectionHeading
        eyebrow="Why NovaSynth"
        title="The whole picture, without the busywork"
        sub="Competitive intel today means manual desk research, a wall of news alerts, or a heavyweight enterprise suite. Here is how NovaSynth is different."
      />

      {/* Comparison - table on desktop, stacked cards on mobile. */}
      <div className="mt-8 ns-reveal" style={{ ["--ns-delay" as string]: "120ms" }}>
        {/* Desktop / tablet table */}
        <div className="hidden md:block overflow-hidden rounded-2xl border border-[#1a2640] bg-[#0d1424]/70">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#1a2640]">
                <th className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Capability
                </th>
                <th className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Manual research
                </th>
                <th className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Alert tools
                </th>
                <th className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Enterprise suites
                </th>
                <th className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#22d3ee] bg-[#22d3ee]/[0.04]">
                  NovaSynth
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr
                  key={row.capability}
                  className={i < COMPARE_ROWS.length - 1 ? "border-b border-[#1a2640]/60" : ""}
                >
                  <td className="px-5 py-4 text-sm font-medium text-slate-200">{row.capability}</td>
                  <td className="px-5 py-4"><CompareCell cell={row.manual} /></td>
                  <td className="px-5 py-4"><CompareCell cell={row.alerts} /></td>
                  <td className="px-5 py-4"><CompareCell cell={row.suite} /></td>
                  <td className="px-5 py-4 bg-[#22d3ee]/[0.04]"><CompareCell cell={row.nova} emphasise /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards (one card per capability) */}
        <div className="md:hidden flex flex-col gap-3">
          {COMPARE_ROWS.map((row) => (
            <div key={row.capability} className="rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-4">
              <p className="text-sm font-semibold text-white">{row.capability}</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Manual</p>
                  <CompareCell cell={row.manual} />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Alert tools</p>
                  <CompareCell cell={row.alerts} />
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">Suites</p>
                  <CompareCell cell={row.suite} />
                </div>
                <div className="rounded-lg bg-[#22d3ee]/[0.05] -m-1 p-1">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#22d3ee]">NovaSynth</p>
                  <CompareCell cell={row.nova} emphasise />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Capabilities - the concrete deliverables. */}
      <div className="mt-12">
        <SectionHeading
          eyebrow="What you get"
          title="Everything a competitive brief should be"
          sub="Each run delivers the same set of finished, source-backed deliverables - ready to act on, not raw to wrangle."
        />
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPABILITIES.map((c, i) => (
            <div
              key={c.title}
              className="ns-reveal ns-glass rounded-2xl p-6 transition-transform duration-200 hover:-translate-y-0.5"
              style={{ ["--ns-delay" as string]: `${120 + i * 70}ms` }}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
                <c.Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 text-[15px] font-semibold text-white">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="mt-20 scroll-mt-28">
      <SectionHeading eyebrow="FAQ" title="Questions, answered" />
      <div className="mt-7 mx-auto max-w-3xl flex flex-col gap-3">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div
              key={i}
              className="ns-reveal ns-glass rounded-2xl overflow-hidden"
              style={{ ["--ns-delay" as string]: `${100 + i * 60}ms` }}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer group"
              >
                <span className="text-sm font-semibold text-white group-hover:text-[#22d3ee] transition-colors">
                  {f.q}
                </span>
                <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#1a2640] bg-white/5 text-slate-400 shrink-0">
                  {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </span>
              </button>
              {isOpen && (
                <p className="px-5 pb-5 -mt-1 text-sm leading-relaxed text-slate-400 animate-fade-in">{f.a}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Final CTA band ────────────────────────────────────────────────────────────

function FinalCta({ onLaunch }: { onLaunch: (c?: string) => void }) {
  return (
    <section className="mt-20">
      <div className="relative ns-reveal overflow-hidden rounded-3xl border border-brand-600/30 bg-gradient-to-br from-brand-700/25 via-[#0d1424] to-[#22d3ee]/10 p-10 sm:p-14 text-center">
        <div className="ns-aurora" aria-hidden="true" />
        <div className="relative">
          <h2 className="font-mono text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Stop guessing. <span className="ns-gradient-text">Start tracking.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300">
            Your first threat-scored brief is one company name away. Watch the specialists work,
            then act on what they find.
          </p>
          <div className="mt-7 flex justify-center">
            <button
              onClick={() => onLaunch()}
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-7 py-3.5 text-sm font-semibold text-white shadow-glow-brand transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
            >
              Start an analysis
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Shared section heading ────────────────────────────────────────────────────

function SectionHeading({
  eyebrow,
  title,
  sub,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  align?: "center" | "left";
}) {
  const center = align === "center";
  return (
    <div className={`ns-reveal ${center ? "text-center mx-auto max-w-2xl" : "text-left"}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#22d3ee]">{eyebrow}</span>
      <h2 className="mt-2 font-mono text-2xl sm:text-3xl font-bold tracking-tight text-white">{title}</h2>
      {sub && <p className={`mt-3 text-sm leading-relaxed text-slate-400 ${center ? "" : "max-w-2xl"}`}>{sub}</p>}
    </div>
  );
}
